/**
 * OAuth Popup Utility (Story 4.5, Task 5.2, 5.3, 5.4)
 *
 * Opens a popup window for OAuth flows with postMessage communication.
 * Falls back to full-page redirect when popups are blocked or on mobile.
 *
 * Security:
 * - Validates event.origin against APP_URL
 * - Validates payload against Zod schema
 * - Discards unknown origins and invalid payloads silently
 */

import {
  INTEGRATION_OAUTH_POST_MESSAGE_SCHEMA,
} from "@provenleap/integrations";
import { apiClient } from "./apiClient";

// ============================================================================
// Types
// ============================================================================

export interface OAuthPopupResult {
  type: "oauth-success";
  provider: string;
  platforms: string[];
  existing?: string[];
  /**
   * Story 32-3 (AC #11): total cataloged-resource count from multi-tenant
   * callbacks (count ONLY — never the catalog). `> 1` means the consumer
   * should offer the CatalogPickerDialog for the freshly discovered
   * resources. Schema-validated via INTEGRATION_OAUTH_POST_MESSAGE_SCHEMA.
   */
  catalogCount?: number;
}

export interface OAuthPopupError {
  type: "oauth-error";
  provider: string;
  error: string;
  errorCategory?: string;
}

export interface OpenOAuthPopupOptions {
  /**
   * The OAuth URL to navigate the popup to.
   * Accepts a string (direct URL) or a Promise<string> (async fetch).
   * When a Promise is used, window.open("about:blank") is called synchronously
   * (Safari requirement), then the popup navigates after the promise resolves.
   */
  authUrl: string | Promise<string>;
  /** The expected origin for postMessage validation */
  appUrl: string;
  /** Timeout in ms before the popup is considered abandoned (default: 5 min) */
  timeoutMs?: number;
}

// ============================================================================
// Mobile Detection
// ============================================================================

/**
 * Detect mobile devices via touch capability.
 * Per story spec: detect via `'ontouchstart' in window`.
 */
export function isMobileDevice(): boolean {
  return "ontouchstart" in window;
}

// ============================================================================
// Popup Utility
// ============================================================================

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;
const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const POLL_INTERVAL_MS = 500;

/**
 * Opens an OAuth popup and waits for postMessage result.
 *
 * Safari requires window.open() to be called synchronously in the click handler.
 * Opens about:blank first, then navigates to the auth URL.
 *
 * Returns a Promise that resolves on success or rejects on error/close/timeout.
 */
export function openOAuthPopup(
  options: OpenOAuthPopupOptions
): {
  promise: Promise<OAuthPopupResult>;
  cancel: () => void;
} {
  const { authUrl, appUrl, timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  // Center the popup on screen
  const left = Math.round(
    window.screenX + (window.innerWidth - POPUP_WIDTH) / 2
  );
  const top = Math.round(
    window.screenY + (window.innerHeight - POPUP_HEIGHT) / 2
  );
  const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`;

  // Open about:blank synchronously (Safari requirement), then navigate
  const maybePopup = window.open("about:blank", "provenleap-oauth", features);

  let cancelled = false;

  if (!maybePopup) {
    // Popup blocked — fall back to full-page redirect
    const promise = Promise.reject(
      new OAuthPopupBlockedError("Pop-up was blocked")
    );
    return {
      promise,
      cancel: () => {},
    };
  }

  // After null check, popup is guaranteed to be non-null
  const popup: Window = maybePopup;

  const promise = new Promise<OAuthPopupResult>((resolve, reject) => {
    let settled = false;

    // Navigate popup to auth URL (resolve promise if async)
    Promise.resolve(authUrl)
      .then((resolvedUrl) => {
        if (!settled) {
          // Mark this window as an OAuth popup via the URL fragment so the callback
          // page can detect it even after COOP severs window.name/window.opener
          const url = new URL(resolvedUrl);
          popup.location.href = url.toString();
        }
      })
      .catch((err) => {
        if (settled) return;
        settled = true;
        cleanup();
        popup.close();
        reject(err);
      });

    // Set up timeout
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      popup.close();
      reject(new Error("OAuth flow timed out after 5 minutes"));
    }, timeoutMs);

    // Poll for popup closure
    const pollId = setInterval(() => {
      try {
        if (popup.closed && !settled) {
          settled = true;
          cleanup();
          reject(new Error("OAuth window was closed"));
        }
      } catch {
        // COOP may block access to popup.closed — ignore
      }
    }, POLL_INTERVAL_MS);

    // Listen for postMessage
    function handleMessage(event: MessageEvent) {
      // Validate origin
      if (event.origin !== appUrl) {
        return; // Silently discard unknown origins
      }

      // Validate payload against Zod schema
      const parsed = INTEGRATION_OAUTH_POST_MESSAGE_SCHEMA.safeParse(
        event.data
      );
      if (!parsed.success) {
        return; // Silently discard invalid payloads
      }

      if (settled) return;
      settled = true;
      cleanup();

      const data = parsed.data;
      if (data.type === "oauth-success") {
        resolve({
          type: "oauth-success",
          provider: data.provider,
          platforms: data.platforms,
          existing: data.existing,
          catalogCount: data.catalogCount,
        });
      } else {
        reject(
          new OAuthFlowError(
            data.error,
            "errorCategory" in data ? data.errorCategory : undefined
          )
        );
      }

      // Close the popup after processing
      popup.close();
    }

    // Register message listener BEFORE popup navigates
    window.addEventListener("message", handleMessage);

    function cleanup() {
      window.removeEventListener("message", handleMessage);
      clearInterval(pollId);
      clearTimeout(timeoutId);
    }
  });

  return {
    promise,
    cancel: () => {
      if (!cancelled) {
        cancelled = true;
        popup.close();
      }
    },
  };
}

// ============================================================================
// Error Classes
// ============================================================================

export class OAuthPopupBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OAuthPopupBlockedError";
  }
}

export class OAuthFlowError extends Error {
  readonly errorCategory?: string;

  constructor(message: string, errorCategory?: string) {
    super(message);
    this.name = "OAuthFlowError";
    this.errorCategory = errorCategory;
  }
}

// ============================================================================
// Fetch Auth URL Helper
// ============================================================================

/**
 * Story 32.2 — options shared by the per-family auth-url fetch helpers.
 * `forceNewGrant: true` is included in the POST body and makes the server
 * launch a fresh OAuth flow that NEVER reuses/overwrites an existing grant
 * (google/meta/linkedin/tiktok honor it server-side; the other families'
 * non-strict schemas strip it — their callbacks already dedup by provider
 * account, so "add another grant" is simply their existing connect flow).
 */
export interface FetchAuthUrlOptions {
  forceNewGrant?: boolean;
}

/**
 * Fetches the Google OAuth URL from the server via an authenticated POST.
 * Uses apiClient to automatically attach the Bearer token.
 *
 * This replaces the old buildIntegrationAuthUrl which returned a server URL
 * that the popup would navigate to. That approach failed because browser
 * navigation (popup.location.href) cannot send Authorization headers.
 *
 * Now the server returns the Google URL as JSON, and the popup navigates
 * directly to Google.
 */
export async function fetchIntegrationAuthUrl(
  clientId: string,
  scopes: string[],
  returnUrl?: string,
  opts?: FetchAuthUrlOptions
): Promise<string> {
  const response = await apiClient("/api/integrations/google/auth-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientId,
      scopes: scopes.join(","),
      ...(returnUrl ? { returnUrl } : {}),
      ...(opts?.forceNewGrant ? { forceNewGrant: true } : {}),
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(
      (data as { detail?: string }).detail ?? "Failed to initiate OAuth"
    );
  }

  const data = (await response.json()) as { url: string };
  return data.url;
}

/**
 * Fetches the Meta OAuth URL from the server via an authenticated POST.
 * Meta OAuth requests all scopes at once (no per-scope selection).
 */
export async function fetchMetaAuthUrl(
  clientId: string,
  returnUrl?: string,
  opts?: FetchAuthUrlOptions
): Promise<string> {
  const response = await apiClient("/api/integrations/meta/auth-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientId,
      ...(returnUrl ? { returnUrl } : {}),
      ...(opts?.forceNewGrant ? { forceNewGrant: true } : {}),
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(
      (data as { detail?: string }).detail ?? "Failed to initiate Meta OAuth"
    );
  }

  const data = (await response.json()) as { url: string };
  return data.url;
}

/**
 * Fetches the LinkedIn OAuth URL from the server via an authenticated POST.
 * LinkedIn OAuth requests all scopes at once (no per-scope selection).
 */
export async function fetchLinkedInAuthUrl(
  clientId: string,
  returnUrl?: string,
  opts?: FetchAuthUrlOptions
): Promise<string> {
  const response = await apiClient("/api/integrations/linkedin/auth-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientId,
      ...(returnUrl ? { returnUrl } : {}),
      ...(opts?.forceNewGrant ? { forceNewGrant: true } : {}),
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(
      (data as { detail?: string }).detail ?? "Failed to initiate LinkedIn OAuth"
    );
  }

  const data = (await response.json()) as { url: string };
  return data.url;
}

/**
 * Fetches the TikTok OAuth URL from the server via an authenticated POST.
 * TikTok OAuth requests all scopes at once (no per-scope selection).
 */
export async function fetchTikTokAuthUrl(
  clientId: string,
  returnUrl?: string,
  opts?: FetchAuthUrlOptions
): Promise<string> {
  const response = await apiClient("/api/integrations/tiktok/auth-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientId,
      ...(returnUrl ? { returnUrl } : {}),
      ...(opts?.forceNewGrant ? { forceNewGrant: true } : {}),
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(
      (data as { detail?: string }).detail ?? "Failed to initiate TikTok OAuth"
    );
  }

  const data = (await response.json()) as { url: string };
  return data.url;
}

/**
 * Fetches the Mailchimp OAuth URL from the server via an authenticated POST.
 * Mailchimp OAuth grants full access (no scopes).
 */
export async function fetchMailchimpAuthUrl(
  clientId: string,
  returnUrl?: string,
  opts?: FetchAuthUrlOptions
): Promise<string> {
  const response = await apiClient("/api/integrations/mailchimp/auth-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientId,
      ...(returnUrl ? { returnUrl } : {}),
      ...(opts?.forceNewGrant ? { forceNewGrant: true } : {}),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { detail?: string }).detail ?? "Failed to initiate Mailchimp OAuth"
    );
  }

  const result = (await response.json()) as { url: string };
  return result.url;
}

/**
 * Fetches the Klaviyo OAuth URL from the server via an authenticated POST.
 * Klaviyo OAuth uses PKCE (code_challenge generated server-side).
 */
export async function fetchKlaviyoAuthUrl(
  clientId: string,
  returnUrl?: string,
  opts?: FetchAuthUrlOptions
): Promise<string> {
  const response = await apiClient("/api/integrations/klaviyo/auth-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientId,
      ...(returnUrl ? { returnUrl } : {}),
      ...(opts?.forceNewGrant ? { forceNewGrant: true } : {}),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { detail?: string }).detail ?? "Failed to initiate Klaviyo OAuth"
    );
  }

  const result = (await response.json()) as { url: string };
  return result.url;
}

/**
 * Fetches the Microsoft Advertising OAuth URL from the server (Story 19.1).
 * Microsoft uses OAuth 2.0 + PKCE (server generates code_verifier/challenge).
 * Full-page redirect flow (matches Klaviyo).
 */
export async function fetchMicrosoftAdsAuthUrl(
  clientId: string,
  returnUrl?: string
): Promise<string> {
  const response = await apiClient(
    "/api/integrations/microsoft-ads/auth-url",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId,
        ...(returnUrl ? { returnUrl } : {}),
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { detail?: string }).detail ??
        "Failed to initiate Microsoft Advertising OAuth"
    );
  }

  const result = (await response.json()) as { url: string };
  return result.url;
}

/**
 * Fetches the Pinterest Advertising OAuth URL from the server (Story 19.4).
 * Pinterest uses OAuth 2.0 + PKCE (server generates code_verifier/challenge).
 * Full-page redirect flow (matches Klaviyo + Microsoft Ads).
 */
export async function fetchPinterestAdsAuthUrl(
  clientId: string,
  returnUrl?: string
): Promise<string> {
  const response = await apiClient(
    "/api/integrations/pinterest-ads/auth-url",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId,
        ...(returnUrl ? { returnUrl } : {}),
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { detail?: string }).detail ??
        "Failed to initiate Pinterest Advertising OAuth"
    );
  }

  const result = (await response.json()) as { url: string };
  return result.url;
}

/**
 * Fetches the Snapchat Advertising OAuth URL from the server (Story 19.7).
 * Snap uses OAuth 2.0 + PKCE (server generates code_verifier/challenge) with
 * form-body credentials on the token endpoint. Full-page redirect flow.
 */
export async function fetchSnapchatAdsAuthUrl(
  clientId: string,
  returnUrl?: string
): Promise<string> {
  const response = await apiClient(
    "/api/integrations/snapchat-ads/auth-url",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId,
        ...(returnUrl ? { returnUrl } : {}),
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { detail?: string }).detail ??
        "Failed to initiate Snapchat Advertising OAuth"
    );
  }

  const result = (await response.json()) as { url: string };
  return result.url;
}

/**
 * Fetches the Reddit Advertising OAuth URL from the server (Story 19.10).
 * Reddit uses OAuth 2.0 + PKCE with HTTP Basic Auth on the token endpoint
 * (matches Pinterest, diverges from Snapchat / MS Ads / Klaviyo). Server
 * generates code_verifier/challenge. Full-page redirect flow.
 */
export async function fetchRedditAdsAuthUrl(
  clientId: string,
  returnUrl?: string
): Promise<string> {
  const response = await apiClient(
    "/api/integrations/reddit-ads/auth-url",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId,
        ...(returnUrl ? { returnUrl } : {}),
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { detail?: string }).detail ??
        "Failed to initiate Reddit Advertising OAuth"
    );
  }

  const result = (await response.json()) as { url: string };
  return result.url;
}

/**
 * Fetches the Amazon Advertising OAuth URL from the server (Story 19.13).
 * Amazon uses Login with Amazon (LWA) OAuth 2.0 + PKCE with HTTP Basic Auth on
 * the token endpoint (matches Pinterest 19.4 / Reddit 19.10, diverges from
 * Snapchat / MS Ads / Klaviyo). Server generates code_verifier/challenge.
 * Full-page redirect flow. Amazon-specific: profiles are region-partitioned
 * (NA/EU/FE) — discovery latency is ~5-10s for accounts in multiple regions.
 */
export async function fetchAmazonAdsAuthUrl(
  clientId: string,
  returnUrl?: string
): Promise<string> {
  const response = await apiClient(
    "/api/integrations/amazon-ads/auth-url",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId,
        ...(returnUrl ? { returnUrl } : {}),
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { detail?: string }).detail ??
        "Failed to initiate Amazon Ads OAuth"
    );
  }

  const result = (await response.json()) as { url: string };
  return result.url;
}

/**
 * Fetch HubSpot OAuth authorization URL from the server.
 * HubSpot uses confidential client flow (no PKCE) — server handles client_secret.
 */
export async function fetchHubSpotAuthUrl(
  clientId: string,
  returnUrl?: string,
  opts?: FetchAuthUrlOptions
): Promise<string> {
  const response = await apiClient("/api/integrations/hubspot/auth-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientId,
      ...(returnUrl ? { returnUrl } : {}),
      ...(opts?.forceNewGrant ? { forceNewGrant: true } : {}),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { detail?: string }).detail ?? "Failed to initiate HubSpot OAuth"
    );
  }

  const result = (await response.json()) as { url: string };
  return result.url;
}

/**
 * Fetch Shopify OAuth authorization URL from the server.
 *
 * Unlike other providers, Shopify requires a shopDomain to construct
 * the store-specific auth URL.
 *
 * @param clientId - ProvenLeap client UUID
 * @param shopDomain - Normalized Shopify store domain (e.g., "mystore.myshopify.com")
 * @returns The Shopify OAuth authorization URL for full-page redirect
 */
export async function fetchShopifyAuthUrl(
  clientId: string,
  shopDomain: string,
  returnUrl?: string,
  opts?: FetchAuthUrlOptions
): Promise<string> {
  const response = await apiClient("/api/integrations/shopify/auth-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientId,
      shopDomain,
      ...(returnUrl ? { returnUrl } : {}),
      ...(opts?.forceNewGrant ? { forceNewGrant: true } : {}),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { detail?: string }).detail ?? "Failed to initiate Shopify OAuth"
    );
  }

  const result = (await response.json()) as { url: string };
  return result.url;
}
